import NumberFormat from 'react-number-format';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Pagination } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { capitalize } from 'lodash';
import { BookingAction, SearchStatus } from '../../../constants';
import { deleteTripRequest, searchTripsRequest } from '../../../store/slices/trip';

import Button from '../../Button';
import ConfirmModal from '../../ConfirmModal';
import InfoDropdownPopover from '../../InfoDropdownPopover';
import MarkdownToHtml from '../../MarkdownToHtml';
import formatBookingStatus from '../../../helpers/formatters/formatBookingStatus';
import FlightTicketingStatus from '../../TravelOption/FlightInformation/FlightItinerary/FlightInfo/FlightTicketingStatus/FlightTicketingStatus';

const TripStatusStyle = {
  [BookingAction.BOOK]: 'text-green-800 bg-green-200',
  [BookingAction.HOLD]: 'text-red-800 bg-red-200',
  [BookingAction.SEND_QUOTE]: 'text-blue-800 bg-blue-200',
  [BookingAction.CANCELLED]: 'text-orange-800 bg-orange-200',
  [BookingAction.BROCHURE]: 'text-teal-800 bg-teal-200',
  [BookingAction.ADVICE]: 'text-teal-600 bg-teal-200',
  [BookingAction.REQUEST_TO_BOOK]: 'text-white bg-red-500',
};

const useStyles = makeStyles(() => ({
  ul: {
    '& .MuiButtonBase-root': {
      background: 'white',
    },
    '& .Mui-selected': {
      border: '1px solid #0052CC',
      color: '#0052CC',
    },
    '& .Mui-disabled': {
      backgroundColor: 'rgba(143, 146, 161, 0.4)',
    },
  },
}));

const TripList = ({ doAllowDeleteBrochure, doAllowDeleteQuote }) => {
  const { tripsSearch, searchTripsStatus, deleteTripStatus, deleteTripError } = useSelector(
    (store) => store.trip,
  );
  const { travelAgency } = useSelector((store) => store.user);
  const brochureSettings =
    (travelAgency && travelAgency.settings && travelAgency.settings.brochureSettings) || {};
  const brochureDisplayName = brochureSettings.DisplayName || 'Brochure';
  const brochureDisplayNameLower = brochureDisplayName.toLowerCase();
  const [cancellationPolicies, setCancellationPolicies] = useState({});
  const dispatch = useDispatch();
  const classes = useStyles();
  const router = useRouter();
  const { debug } = router.query;
  const [tripToDelete, setTripToDelete] = useState(null);

  const handleChangePage = (event, page) => {
    const searchObj = {
      ...tripsSearch.searchObj,
      page,
      debug: debug === 'true',
    };
    dispatch(searchTripsRequest({ searchObj }));
  };

  const handleCancelConfirm = () => {
    setTripToDelete(null);
  };

  const displayPolicyList = (policyType, policyList) => {
    if (policyType && policyList && policyList.length > 0) {
      return (
        <div className="text-left font-normal text-xs">
          <div className="font-bold text-black">{policyType}:</div>
          <hr className="my-1" />
          <div className="policy-list-container" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <ul>
              {policyList.map((policy) => (
                <li key={policy || ''} className="text-black text-xs">
                  <span>{(policy || '').replace(/:/g, ':\n')}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    return null;
  };

  const extractPolicies = (data, type) => {
    try {
      if (data && data[type]) {
        return data[type].split('<p>').map((item) => item.replace('</p>', '').trim());
      }
    } catch (e) {
      console.error(e);
    }
    return [];
  };

  const cancellationHandler = (tripId) => {
    const cancellationPoliciesString =
      tripsSearch.trips &&
      tripsSearch.trips[tripId] &&
      tripsSearch.trips[tripId].cancellationPolicies;

    if (cancellationPolicies) {
      const parsedCancellationData = JSON.parse(cancellationPoliciesString);

      const policies = {
        flight: extractPolicies(parsedCancellationData, 'flight'),
        hotel: extractPolicies(parsedCancellationData, 'hotel'),
        carrental: extractPolicies(parsedCancellationData, 'carrental'),
        tour: extractPolicies(parsedCancellationData, 'tour'),
      };

      setCancellationPolicies(policies);
    } else {
      setCancellationPolicies('');
    }
  };

  const handleShowConfirmModal = (trip) => {
    setTripToDelete(trip);
  };

  const handleDeleteTrip = () => {
    dispatch(
      deleteTripRequest({
        tripId: tripToDelete.tripId,
        tripType: tripToDelete.tripStatus,
      }),
    );
    setTripToDelete(null);
  };

  return (
    <div>
      {tripsSearch &&
        tripsSearch.trips &&
        tripsSearch.trips.map((trip, index) => (
          <div
            className="border border-gray-300 bg-white shadow rounded-md w-full mx-auto mb-5 grid xl:grid-cols-4 grid-cols-3 p-4"
            key={trip.tripId}
          >
            <div className="col-span-3">
              <span className="font-bold text-lg">
                <span className="text-subHeading font-subHeading">{trip.title}</span>
              </span>
              <span
                className={`${TripStatusStyle[trip.tripStatus] || 'text-gray-800 bg-gray-200'} text-xs px-2 py-1 rounded-md ml-4 whitespace-nowrap`}
              >
                {trip.tripStatus === BookingAction.BROCHURE
                  ? brochureDisplayName
                  : formatBookingStatus(trip.tripStatus)}
              </span>
              {trip.shortTitle && <div className="text-xs text-gray-600">{trip.shortTitle}</div>}
              {trip.longTripTitle && (
                <div className="text-xs text-gray-600">{trip.longTripTitle}</div>
              )}
              <div className="text-xs mt-2 leading-5">
                {trip.subject && (
                  <div>
                    <span className="text-gray-600">Subject:</span> {trip.subject}
                  </div>
                )}
                <div>
                  <span className="text-gray-600">
                    {`${trip.tripStatus === BookingAction.BROCHURE ? `${brochureDisplayName} create date:` : `${`${trip.tripStatus === BookingAction.SEND_QUOTE ? 'Trip quote create date' : 'Trip booking date'} :`}`}`}
                  </span>{' '}
                  {dayjs(trip.tripCreatedTime).format('MMM D, YYYY')}
                </div>
                {trip.tripStatus !== BookingAction.BROCHURE && (
                  <div>
                    <span className="text-gray-600">Trip start date:</span>{' '}
                    {dayjs(trip.tripStartDate).format('MMM D, YYYY')}
                  </div>
                )}
                {trip.tripStatus !== BookingAction.BROCHURE && trip.tripEndDate && (
                  <div>
                    <span className="text-gray-600">Trip end date:</span>{' '}
                    {dayjs(trip.tripEndDate).format('MMM D, YYYY')}
                  </div>
                )}
                {trip.shortTitle?.toLowerCase()?.includes('flight') &&
                  (trip.tripStatus === BookingAction.BOOK ||
                    trip.tripStatus === BookingAction.HOLD ||
                    trip.tripStatus === BookingAction.CANCELLED) && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-600">Flight Ticketing Status:</span>
                      <FlightTicketingStatus key={trip.id} trip={trip} />
                    </div>
                  )}
                {trip.travelers && trip.travelers.length > 0 ? (
                  <div>
                    <span className="text-gray-600">Customer:</span> {trip.travelers.join(', ')}
                  </div>
                ) : (
                  ''
                )}
                {trip.tripStatus === BookingAction.ADVICE ? (
                  <>
                    {trip.customerEmail && (
                      <div>
                        <span className="text-gray-600">Customer email:</span> {trip.customerEmail}
                      </div>
                    )}
                    {trip.customerPhoneNumber && (
                      <div>
                        <span className="text-gray-600">Customer phone:</span>{' '}
                        {trip.customerPhoneNumber}
                      </div>
                    )}
                  </>
                ) : (
                  ''
                )}
                {trip.tripSentTo ? (
                  <div>
                    <span className="text-gray-600">Sent to:</span> {trip.tripSentTo}
                  </div>
                ) : (
                  ''
                )}
                <div>
                  <span className="text-gray-600">
                    {trip.tripStatus === BookingAction.SEND_QUOTE ||
                    trip.tripStatus === BookingAction.BROCHURE ||
                    trip.tripStatus === BookingAction.ADVICE
                      ? 'Sent by'
                      : 'Booked by'}
                    :
                  </span>{' '}
                  {trip.tripBookedBy}
                </div>
                {trip.gdsRecordLocator ? (
                  <div>
                    <span className="text-gray-600">GDS record locator:</span>{' '}
                    {trip.gdsRecordLocator}
                  </div>
                ) : (
                  ''
                )}
                {trip.organization ? (
                  <div>
                    <span className="text-gray-600">Organization:</span> {trip.organization}
                  </div>
                ) : (
                  ''
                )}
                {trip.advisorEmail ? (
                  <div>
                    <span className="text-gray-600">Advisor Email:</span> {trip.advisorEmail}
                  </div>
                ) : (
                  ''
                )}
                {trip.customBookingFields &&
                  Object.keys(trip.customBookingFields || {}).length > 0 &&
                  Object.keys(trip.customBookingFields).map((key) =>
                    key &&
                    trip.customBookingFields[key] &&
                    trip.customBookingFields[key].length > 0 ? (
                      <div key={key}>
                        <span className="text-gray-600">{capitalize(key)}: </span>
                        {Array.isArray(trip.customBookingFields[key])
                          ? trip.customBookingFields[key].join(', ')
                          : trip.customBookingFields[key]}{' '}
                      </div>
                    ) : null,
                  )}
              </div>
            </div>
            <div className="text-right">
              {trip.tripStatus !== BookingAction.BROCHURE && (
                <div className="font-bold text-xl">
                  {trip.tripPriceWithCurrency ? (
                    trip.tripPriceWithCurrency
                  ) : (
                    <NumberFormat
                      value={trip.tripPrice}
                      displayType="text"
                      thousandSeparator
                      prefix="$"
                      fixedDecimalScale="true"
                      decimalScale="2"
                    />
                  )}
                </div>
              )}
              <div>
                <a
                  href={trip.pageLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-theme text-sm"
                >
                  {`View ${trip.tripStatus === BookingAction.BROCHURE ? brochureDisplayNameLower : 'trip'}
                 ${trip.tripStatus === BookingAction.REQUEST_TO_BOOK ? 'and approve' : ''}`}
                </a>
              </div>
              {trip.cancellationPolicies && (
                <InfoDropdownPopover
                  dropdownTextPlaceholder="View Cancellation Policy"
                  onOpen={() => cancellationHandler(index)}
                  className="font-bold text-theme mt-1"
                  fontSize="text-sm"
                >
                  <div className="grid gap-2 p-4">
                    {travelAgency &&
                      travelAgency.settings &&
                      travelAgency.settings.defaultConditionsAndPolicy && (
                        <div>
                          <MarkdownToHtml
                            content={travelAgency.settings.defaultConditionsAndPolicy}
                            className="text-black text-left text-xs font-normal"
                          />
                        </div>
                      )}
                    {displayPolicyList('Flight', cancellationPolicies.flight)}
                    {displayPolicyList('Rental car', cancellationPolicies.carrental)}
                    {displayPolicyList('Hotel', cancellationPolicies.hotel)}
                    {displayPolicyList('Tour', cancellationPolicies.tour)}
                  </div>
                </InfoDropdownPopover>
              )}
              {((trip.tripStatus === BookingAction.BROCHURE && doAllowDeleteBrochure) ||
                (trip.tripStatus === BookingAction.SEND_QUOTE && doAllowDeleteQuote)) && (
                <div>
                  {(!deleteTripStatus[trip.tripId] ||
                    deleteTripStatus[trip.tripId] !== SearchStatus.DONE) && (
                    <Button
                      variant={Button.buttonVariants.OUTLINE}
                      size={Button.buttonSizes.XS}
                      onClick={() => handleShowConfirmModal(trip)}
                      doShowLoadingState={
                        deleteTripStatus && deleteTripStatus[trip.tripId] === SearchStatus.SEARCHING
                      }
                      className="mt-2"
                    >
                      Delete
                    </Button>
                  )}
                  {deleteTripStatus[trip.tripId] &&
                  deleteTripStatus[trip.tripId] === SearchStatus.DONE &&
                  deleteTripError ? (
                    <div className="text-xs text-red-600 font-bold">{deleteTripError}</div>
                  ) : (
                    <></>
                  )}
                  {deleteTripStatus[trip.tripId] &&
                  deleteTripStatus[trip.tripId] === SearchStatus.DONE ? (
                    <div className="text-xs text-green-600 font-bold">Successfully deleted!</div>
                  ) : (
                    <></>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      {searchTripsStatus === SearchStatus.DONE &&
        (!tripsSearch || !tripsSearch.trips || tripsSearch.trips.length === 0) && (
          <div>No trips found!</div>
        )}
      {tripToDelete && (
        <ConfirmModal
          message={`Are you sure to remove the ${
            tripToDelete.tripStatus === BookingAction.BROCHURE
              ? brochureDisplayNameLower
              : formatBookingStatus(tripToDelete.tripStatus).toLowerCase()
          }?`}
          event="Trips page - remove trip"
          onCancel={handleCancelConfirm}
          onConfirm={handleDeleteTrip}
        />
      )}
      {tripsSearch.searchObj.doViewAll &&
        tripsSearch &&
        tripsSearch.filterObj &&
        tripsSearch.filterObj.totalPages > 0 && (
          <div className="text-right">
            <Pagination
              count={tripsSearch.filterObj.totalPages}
              variant="outlined"
              shape="rounded"
              className="float-right"
              page={tripsSearch.searchObj.page}
              onChange={handleChangePage}
              classes={classes}
            />
          </div>
        )}
    </div>
  );
};

TripList.propTypes = {
  doAllowDeleteBrochure: PropTypes.bool,
  doAllowDeleteQuote: PropTypes.bool,
};

TripList.defaultProps = {
  doAllowDeleteBrochure: false,
  doAllowDeleteQuote: false,
};

export default TripList;
